import React, { useState, useEffect } from 'react';
import Confirmation from '../components/Confirmation';
import { IGetConfirmation, IGetConfirmationResponse, IGetExpiredConfirmation } from '@lib/types';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../shared/state';
import { UtagHelper } from '../utils/utagHelper';
import {
  AutoPayTagging,
  ProductTypeTagging,
  getPaymentTypeTagging,
  AccountNameTagging,
  AppFlow,
} from '@lib/web-shared';
import { PolicyHelper } from '@ace-clublabs/payments-helper';
import { TransactionStatus, PaymentType } from '@ace-clublabs/payment-types';
import { ErrorType, IGetPolicyPaymentInfoResponse } from '../shared/types';
import { Pages } from '../shared/pages';
import Spinner from '../components/Spinner';

const ConfirmationPage: React.FC = () => {
  const [isLoading, setLoading] = useState(true);
  const [secondaryEmail] = useGlobalState('secondaryEmail');
  const [policyPaymentInfo] = useGlobalState('policyPaymentInfo');
  const [primaryEmail] = useGlobalState('primaryEmail');
  const [clubTag] = useGlobalState('clubTag');
  const [clubCodeFromState] = useGlobalState('clubCode');
  const [paymentPlan] = useGlobalState('paymentPlan');
  const [policyMetaData] = useGlobalState('policyMetaData');
  const [confirmationDetails, setConfirmationDetails] = useState<IGetConfirmationResponse>(
    {} as IGetConfirmationResponse
  );
  const [appFlow] = useGlobalState('appFlow');
  let navigate = useNavigate();

  const tagConfirmation = (confirmation: IGetConfirmationResponse) => {
    const { minimumDue, allowAutoPay } = policyPaymentInfo as IGetPolicyPaymentInfoResponse;
    const { autoPay, confirmationNumber, amountPaid, paymentType } = confirmation as IGetConfirmation;
    const { expired } = confirmation as IGetExpiredConfirmation;
    const amount = amountPaid ? amountPaid.toFixed(2) : '0.00';
    const isAutoPay = autoPay?.status === TransactionStatus.Succeeded;
    const ins_min_due = minimumDue ? 'true' : 'false';
    const pay_add_amount = `${amountPaid > 0 && !minimumDue}`;
    const ins_autopaycampaign = allowAutoPay ? 'autopay required' : 'autopay not required';
    const ins_policy = policyMetaData.prefix;

    let flowSpecificTags: any;
    if (appFlow === AppFlow.AUTOPAY) {
      flowSpecificTags = {
        category: amountPaid
          ? [AccountNameTagging.InsuranceEasyAutoPay, AccountNameTagging.InsuranceEasyAutoPay]
          : [AccountNameTagging.InsuranceEasyAutoPay],
        product: amountPaid ? [ProductTypeTagging.Insurance, ProductTypeTagging.AutoPay] : [ProductTypeTagging.AutoPay],
        quantity: amountPaid ? ['1', '1'] : ['1'],
        transaction_amount: amountPaid ? [amount, '0.00'] : ['0.00'],
      };
    } else {
      flowSpecificTags = {
        category: isAutoPay
          ? [AccountNameTagging.InsuranceEasyPay, AccountNameTagging.InsuranceEasyPay]
          : [AccountNameTagging.InsuranceEasyPay],
        product: isAutoPay
          ? [ProductTypeTagging.Insurance, ProductTypeTagging.AutoPay]
          : [ProductTypeTagging.Insurance],
        quantity: isAutoPay ? ['1', '1'] : ['1'],
        transaction_amount: isAutoPay ? [amount, '0.00'] : [amount],
        autopay: isAutoPay ? AutoPayTagging.Enrolled : AutoPayTagging.NotEnrolled,
        ins_autopaycampaign,
        pay_add_amount,
      };
    }

    const baseTags = {
      ...UtagHelper.loadCommonTags(appFlow),
      club: clubTag,
      ins_min_due,
      state: policyMetaData.state,
      ins_policy,
      additional_email: !!secondaryEmail,
    };

    const confirmationTags = {
      ...baseTags,
      ...flowSpecificTags,
      page: UtagHelper.getPages(appFlow).confirmationPage,
      confirmation_id: confirmationNumber,
      transaction_type: getPaymentTypeTagging(paymentType as PaymentType),
      paymentplan: paymentPlan,
    };

    const expiredConfirmationTags = {
      ...baseTags,
      page: UtagHelper.getPages(appFlow).expiredConfirmationPage,
      ins_autopaycampaign: appFlow === AppFlow.AUTOPAY ? undefined : ins_autopaycampaign,
    };

    let tagValues = !expired ? confirmationTags : expiredConfirmationTags;

    UtagHelper.tag(tagValues);
  };

  let getConfirmationDetails = async (transactionId: string, productPrefix: string) => {
    setLoading(true);
    try {
      const confirmation = await sources.getConfirmation({
        transactionId,
        productPrefix,
        primaryEmail,
        secondaryEmail,
      });

      setConfirmationDetails({
        ...confirmation,
        primaryEmail: policyPaymentInfo?.email || primaryEmail,
      });

      tagConfirmation(confirmation);

      setLoading(false);
    } catch (e: unknown) {
      navigate(Pages.SorryPage, { state: { errorType: ErrorType.SYSTEM_ERROR } });
    }
  };

  const _clubCode = (confirmationDetails as IGetConfirmation).policyNumber?.startsWith('PAA')
    ? clubCodeFromState
    : PolicyHelper.getProductInfoFromPolicyNumber((confirmationDetails as IGetConfirmation).policyNumber).clubCode ||
      clubCodeFromState;

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const transactionId = urlParams.get('transactionId') || '';
    const productPrefix = urlParams.get('pp') || '';
    getConfirmationDetails(transactionId, productPrefix);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isLoading ? (
    <Spinner />
  ) : (
    <Confirmation {...confirmationDetails} clubCode={_clubCode} isEmailOnFile={!!policyPaymentInfo?.email} />
  );
};

export default ConfirmationPage;
